import React, { useState, useEffect } from 'react';
import './AdditionalInfo.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaInfoCircle,
  FaUserGraduate,
  FaPaperclip,
  FaFileAlt,
  FaExclamationCircle
} from 'react-icons/fa';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
};

const studentName = (s) =>
  `${s?.profile?.firstName || ''} ${s?.profile?.lastName || ''}`.trim() || s?.email || 'Unknown Student';

const AdditionalInfo = ({ assignedCourse }) => {
  const token = localStorage.getItem('token');
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFields = async () => {
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/additional-info/teacher`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setFields(data.fields || []);
      } else {
        setError(data.message || 'Failed to load additional information');
      }
    } catch (err) {
      console.error('Error fetching teacher additional info:', err);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAttachment = async (attachment) => {
    try {
      const res = await fetch(
        `${collegeConfig.api.baseURL}/upload/signed-url?key=${encodeURIComponent(attachment.key)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (res.ok && data.url) {
        window.open(data.url, '_blank');
      }
    } catch (err) {
      console.error('Error viewing attachment:', err);
    }
  };

  const totalSubmissions = fields.reduce((sum, f) => sum + (f.submissions?.length || 0), 0);

  if (loading) {
    return (
      <div className="ai-loading">
        <div className="spinner"></div>
        <p>Loading additional information...</p>
      </div>
    );
  }

  return (
    <div className="ai-container">
      <div className="ai-header">
        <h2><FaInfoCircle style={{ marginRight: 10 }} />Additional Info</h2>
        <p>Information submitted by students of {assignedCourse?.name || 'your department'}</p>
      </div>

      {error && <div className="alert alert-error"><FaExclamationCircle /> {error}</div>}

      {!error && (fields.length === 0 || totalSubmissions === 0) && (
        <div className="ai-empty">
          <FaInfoCircle className="ai-empty-icon" />
          <h3>Nothing submitted yet</h3>
          <p>No additional information has been filled by students in your department yet.</p>
        </div>
      )}

      {totalSubmissions > 0 && (
        <div className="ai-fields">
          {fields.filter(f => f.submissions?.length > 0).map(field => (
            <div key={field._id} className="ai-field-card">
              <div className="ai-field-header">
                <h3>{field.title}</h3>
                <span className="ai-count-badge">{field.submissions.length} submitted</span>
              </div>
              {field.description && <p className="ai-field-desc">{field.description}</p>}

              <div className="ai-submissions">
                {field.submissions.map(sub => (
                  <div key={sub._id} className="ai-submission-row">
                    <div className="ai-student-cell">
                      <FaUserGraduate className="ai-student-icon" />
                      <div>
                        <div className="ai-student-name">{studentName(sub.studentId)}</div>
                        {sub.studentId?.profile?.registrationNumber && (
                          <div className="ai-student-reg">{sub.studentId.profile.registrationNumber}</div>
                        )}
                      </div>
                    </div>
                    <div className="ai-value-cell">
                      <span className="ai-value-text">{sub.value}</span>
                      {sub.attachment?.key && (
                        <button
                          className="ai-attachment-btn"
                          onClick={() => handleViewAttachment(sub.attachment)}
                          title={sub.attachment.name}
                        >
                          <FaPaperclip /> <FaFileAlt style={{ marginLeft: 4 }} />
                        </button>
                      )}
                    </div>
                    <div className="ai-date-cell">{formatDate(sub.updatedAt)}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdditionalInfo;
